import * as React from "react";
import {
  fetchAssessmentAudit,
  fetchEmployeeIdByAuthUserId,
  fetchExtPrsAnswers,
  fetchExtPrsQuestions,
  getCurrentUser,
  markAssessmentQuestionSkipped,
  unmarkAssessmentQuestionSkipped,
  upsertExtPrsAnswer,
} from "../../api/queries";
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Radio,
  RadioGroup,
  FormControlLabel,
  Alert,
} from "@mui/material";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useI18n } from "../../i18n/I18nProvider";
import {
  SkippedQuestionBadge,
  SkippedQuestionNotice,
  skipQuestionButtonSx,
} from "../SkippedQuestionStatus";
import {
  AssessmentHeader,
  AssessmentSectionIntro,
  AssessmentSidebar,
  answerGridSx,
  assessmentContentSx,
  assessmentLayoutSx,
  assessmentOuterSx,
  assessmentOutlineButtonSx,
  assessmentPageSx,
  assessmentPanelSx,
  assessmentPrimaryButtonSx,
  getAnswerOptionSx,
  questionBodySx,
  questionCardSx,
  questionHeaderSx,
  questionLabelSx,
} from "../AssessmentTestLayout";
import { EXTPRS_SECTIONS, buildQuestionSections } from "./questionSections";
import type { EmployeeInfo, ExtPRSQuestion } from "../../types/scores";


function getAnswerOptions(
  answerOptions:
    | { en?: Record<string, number>; de?: Record<string, number> }
    | null
    | undefined,
  lang: "en" | "de",
) {
  const enEntries = Object.entries(answerOptions?.en ?? {});
  const deEntries = Object.entries(answerOptions?.de ?? {});

  return enEntries.map(([enLabel, weight], index) => {
    const matchingDe = deEntries.find(
      ([, deWeight]) => Number(deWeight) === Number(weight),
    );
    const deLabel = matchingDe?.[0] ?? deEntries[index]?.[0] ?? enLabel;

    return {
      value: enLabel, // canonical value stored in DB
      label: lang === "de" ? deLabel : enLabel,
    };
  });
}

export default function ExtPRStest(props: {
  handlePageChange: (page: string) => void;
}) {
  const { handlePageChange } = props;

  const i18n: any = useI18n();
  const lang: "en" | "de" = i18n.lang ?? "en";
  const L = React.useCallback(
    (en: string, de: string) => (lang === "de" ? de : en),
    [lang],
  );

  const UI = React.useMemo(
    () => ({
      headerTitle: L("ExtPRS Assessment", "ExtPRS Assessment"),
      headerSubtitle: L(
        "Practical readiness from supplies, transport, documentation and household resilience.",
        "Praktische Bereitschaft in Bezug auf Vorräte, Transport, Dokumente und Haushaltsresilienz.",
      ),

      chipInfo: L("ExtPRS", "ExtPRS"),

      loading: L("Loading questions…", "Fragen werden geladen…"),
      saving: L("Saving…", "Speichern…"),
      noQuestions: L(
        "No ExtPRS questions found.",
        "Keine ExtPRS-Fragen gefunden.",
      ),

      sidebarTitle: L("Your progress", "Dein Fortschritt"),
      sidebarSections: L("Sections", "Abschnitte"),
      sidebarOverall: (done: number, total: number) =>
        L(
          `${done} of ${total} questions done`,
          `${done} von ${total} Fragen erledigt`,
        ),
      sidebarRemaining: (remaining: number) =>
        remaining === 0
          ? L("All questions completed", "Alle Fragen abgeschlossen")
          : L(
              `${remaining} question${remaining === 1 ? "" : "s"} left`,
              `Noch ${remaining} ${remaining === 1 ? "Frage" : "Fragen"} offen`,
            ),
      sidebarCount: (done: number, total: number) => `${done}/${total}`,
      sidebarSkipped: (skipped: number) =>
        L(`${skipped} skipped`, `${skipped} übersprungen`),
      sidebarEmpty: L("Nothing to show yet.", "Noch nichts anzuzeigen."),
      sidebarOther: L("Further questions", "Weitere Fragen"),

      btnBack: L("Previous", "Zurück"),
      btnContinueEks: L("Continue with EKS", "Weiter mit EKS"),

      errNotAuth: L(
        "Not authenticated. Please log in again.",
        "Nicht angemeldet. Bitte erneut einloggen.",
      ),
      errEmpNotFound: L(
        "Employee record not found for this account.",
        "Kein Mitarbeiterprofil für dieses Konto gefunden.",
      ),
      errLoadQuestions: L(
        "Failed to load ExtPRS questions.",
        "ExtPRS-Fragen konnten nicht geladen werden.",
      ),
      errLoadAnswers: L(
        "Failed to load previous answers.",
        "Vorherige Antworten konnten nicht geladen werden.",
      ),
      errSave: L(
        "Failed to save your answer. Please try again.",
        "Antwort konnte nicht gespeichert werden. Bitte erneut versuchen.",
      ),
      errSkip: L(
        "Failed to skip this question. Please try again.",
        "Frage konnte nicht übersprungen werden. Bitte erneut versuchen.",
      ),
      skipQuestion: L("Skip question", "Frage überspringen"),
      skippedLabel: L("Skipped", "Übersprungen"),
      skippedNotice: L(
        "You skipped this question. Default scoring will apply unless you answer it later.",
        "Du hast diese Frage übersprungen. Standardwert wird verwendet für die Bewertung, solange du sie später nicht beantwortest.",
      ),

      sectionTitle: L(
        "Extended Practical Readiness",
        "Erweiterte praktische Bereitschaft",
      ),
      sectionSubtitle: L(
        "Answer the following questions based on your current household and practical preparedness. Your answers are saved automatically.",
        "Beantworte die folgenden Fragen auf Basis deiner aktuellen Haushalts- und praktischen Vorbereitung. Deine Antworten werden automatisch gespeichert.",
      ),

      qLabel: (n: number) => L(`Question ${n}`, `Frage ${n}`),
    }),
    [L],
  );

  const [employee, setEmployee] = React.useState<EmployeeInfo | null>(null);
  const [questions, setQuestions] = React.useState<ExtPRSQuestion[]>([]);
  const [answers, setAnswers] = React.useState<Record<string, string>>({});
  const [skippedQuestionCodes, setSkippedQuestionCodes] = React.useState<
    string[]
  >([]);
  const [loading, setLoading] = React.useState(true);
  const [savingQuestionId, setSavingQuestionId] = React.useState<string | null>(
    null,
  );
  const [error, setError] = React.useState<string | null>(null);

  const isAnswered = React.useCallback(
    (q: ExtPRSQuestion) => Boolean(answers[q.id]),
    [answers],
  );

  const isSkipped = React.useCallback(
    (q: ExtPRSQuestion) =>
      Boolean(q.question_code && skippedQuestionCodes.includes(q.question_code)),
    [skippedQuestionCodes],
  );

  const answeredTotal = questions.filter(
    (q) => isAnswered(q) || isSkipped(q),
  ).length;

  const sidebarSections = React.useMemo(
    () =>
      buildQuestionSections({
        definitions: EXTPRS_SECTIONS,
        questions,
        lang,
        getCode: (q) => q.question_code,
        isAnswered,
        isSkipped,
        fallbackLabelEn: "Further questions",
        fallbackLabelDe: "Weitere Fragen",
      }),
    [questions, lang, isAnswered, isSkipped],
  );

  React.useEffect(() => {
    let mounted = true;

    async function loadAll() {
      setLoading(true);
      setError(null);

      try {
        const { data: userData, error: userErr } = await getCurrentUser();
        if (userErr || !userData?.user) throw new Error(UI.errNotAuth);

        const authUserId = userData.user.id;

        const { data: empRow, error: empErr } = await fetchEmployeeIdByAuthUserId(authUserId);

        if (empErr) throw empErr;
        if (!empRow) throw new Error(UI.errEmpNotFound);

        const { data: qRows, error: qErr } = await fetchExtPrsQuestions();

        if (qErr) throw new Error(UI.errLoadQuestions);

        const { data: aRows, error: aErr } = await fetchExtPrsAnswers(empRow.id);

        if (aErr) throw new Error(UI.errLoadAnswers);

        const { data: auditRow } = await fetchAssessmentAudit(empRow.id);

        const answerMap: Record<string, string> = {};
        for (const row of aRows ?? []) {
          answerMap[row.extprs_question_id] = row.given_answer;
        }

        if (!mounted) return;

        setEmployee({ id: empRow.id });
        setQuestions((qRows ?? []) as ExtPRSQuestion[]);
        setAnswers(answerMap);
        setSkippedQuestionCodes(
          (auditRow?.skipped_questions ?? []) as string[],
        );
      } catch (e: any) {
        if (!mounted) return;
        setError(e?.message ?? UI.errLoadQuestions);
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    }

    loadAll();

    return () => {
      mounted = false;
    };
  }, [
    UI.errNotAuth,
    UI.errEmpNotFound,
    UI.errLoadQuestions,
    UI.errLoadAnswers,
  ]);

  async function saveAnswer(question: ExtPRSQuestion, givenAnswer: string) {
    if (!employee) return;

    setSavingQuestionId(question.id);
    setError(null);

    const previousAnswer = answers[question.id];
    const previousSkipped = skippedQuestionCodes;
    const questionCode = question.question_code;

    setAnswers((old) => ({
      ...old,
      [question.id]: givenAnswer,
    }));

    if (questionCode) {
      setSkippedQuestionCodes((old: string[]) =>
        old.filter((code: string) => code !== questionCode),
      );
    }

    const { error: saveErr } = await upsertExtPrsAnswer(employee.id, question.id, givenAnswer);

    if (saveErr) {
      setAnswers((old) => {
        const next = { ...old };
        if (previousAnswer) next[question.id] = previousAnswer;
        else delete next[question.id];
        return next;
      });
      setSkippedQuestionCodes(previousSkipped);
      setSavingQuestionId(null);
      setError(UI.errSave);
      return;
    }

    if (questionCode) {
      const { error: unskipErr } = await unmarkAssessmentQuestionSkipped(employee.id, questionCode);

      if (unskipErr) {
        setSkippedQuestionCodes(previousSkipped);
        setError(UI.errSave);
      }
    }

    setSavingQuestionId(null);
  }

  async function skipQuestion(question: ExtPRSQuestion) {
    if (!employee || !question.question_code) return;

    setSavingQuestionId(question.id);
    setError(null);

    const questionCode = question.question_code;
    const previousAnswer = answers[question.id];
    const previousSkipped = skippedQuestionCodes;
    const nextSkipped = Array.from(new Set([...previousSkipped, questionCode]));

    setAnswers((old) => {
      const next = { ...old };
      delete next[question.id];
      return next;
    });
    setSkippedQuestionCodes(nextSkipped);

    const { error: skipErr } = await markAssessmentQuestionSkipped(employee.id, questionCode);

    setSavingQuestionId(null);

    if (skipErr) {
      setAnswers((old) => {
        const next = { ...old };
        if (previousAnswer) next[question.id] = previousAnswer;
        return next;
      });
      setSkippedQuestionCodes(previousSkipped);
      setError(UI.errSkip);
    }
  }

  function goBack() {
    handlePageChange("PBGtest");
  }

  function goNext() {
    handlePageChange("EKStest");
  }

  return (
    <Box sx={assessmentPageSx}>
      <Box sx={assessmentOuterSx}>
        <AssessmentHeader
          icon={<TuneOutlinedIcon />}
          title={UI.headerTitle}
          subtitle={UI.headerSubtitle}
        />

        <Box sx={assessmentLayoutSx}>
          <AssessmentSidebar
            title={UI.sidebarTitle}
            sections={sidebarSections}
            completed={answeredTotal}
            total={questions.length}
            overallLabel={UI.sidebarOverall}
            remainingLabel={UI.sidebarRemaining}
            sectionsHeading={UI.sidebarSections}
            countLabel={UI.sidebarCount}
            skippedLabel={UI.sidebarSkipped}
            emptyLabel={loading ? UI.loading : UI.sidebarEmpty}
          />

          <Paper elevation={0} sx={assessmentPanelSx}>
            <Box sx={assessmentContentSx}>
              <Stack spacing={2.25}>
                <AssessmentSectionIntro
                  eyebrow="ExtPRS"
                  title={UI.sectionTitle}
                  subtitle={UI.sectionSubtitle}
                />

                {loading ? (
                  <Typography sx={{ fontWeight: 900 }}>{UI.loading}</Typography>
                ) : error ? (
                  <Alert severity="error">{error}</Alert>
                ) : questions.length === 0 ? (
                  <Alert severity="info">{UI.noQuestions}</Alert>
                ) : (
                  <Stack spacing={2}>
                    {questions.map((question, index) => (
                      <QuestionCard
                        key={question.id}
                        question={question}
                        qLabel={UI.qLabel(index + 1)}
                        selectedAnswer={answers[question.id] ?? ""}
                        skipped={
                          question.question_code
                            ? skippedQuestionCodes.includes(
                                question.question_code,
                              )
                            : false
                        }
                        lang={lang}
                        saving={savingQuestionId === question.id}
                        skipLabel={UI.skipQuestion}
                        skippedLabel={UI.skippedLabel}
                        skippedNotice={UI.skippedNotice}
                        onChange={(value) => saveAnswer(question, value)}
                        onSkip={() => skipQuestion(question)}
                      />
                    ))}
                  </Stack>
                )}

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{ pt: 1 }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={goBack}
                    disabled={loading || Boolean(savingQuestionId)}
                    sx={assessmentOutlineButtonSx}
                  >
                    {UI.btnBack}
                  </Button>

                  <Button
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    disabled={loading || Boolean(savingQuestionId)}
                    onClick={goNext}
                    sx={assessmentPrimaryButtonSx}
                  >
                    {UI.btnContinueEks}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}

function QuestionCard(props: {
  question: ExtPRSQuestion;
  selectedAnswer: string;
  lang: "en" | "de";
  skipped: boolean;
  saving: boolean;
  qLabel: string;
  skipLabel: string;
  skippedLabel: string;
  skippedNotice: string;
  onChange: (value: string) => void;
  onSkip: () => void;
}) {
  const {
    question,
    selectedAnswer,
    skipped,
    lang,
    saving,
    qLabel,
    skipLabel,
    skippedLabel,
    skippedNotice,
    onChange,
    onSkip,
  } = props;

  const options = getAnswerOptions(question.answer_options, lang);

  return (
    <Box sx={questionCardSx}>
      <Box sx={questionHeaderSx}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
          <Typography sx={questionLabelSx}>{qLabel}</Typography>

          {skipped && <SkippedQuestionBadge label={skippedLabel} />}
        </Stack>

        <Typography
          sx={{
            fontWeight: 900,
            color: "#111827",
            fontSize: { xs: 18, md: 20 },
            lineHeight: 1.35,
          }}
        >
          {lang === "de" ? question.question_de : question.question_en}
        </Typography>
      </Box>

      <Box sx={questionBodySx}>
        <RadioGroup
          value={selectedAnswer}
          onChange={(e) => onChange(e.target.value)}
        >
          <Box sx={answerGridSx}>
            {options.map((option) => {
              const selected = selectedAnswer === option.value;

              return (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  disabled={saving}
                  control={<Radio />}
                  label={
                    <Typography
                      sx={{
                        fontWeight: selected ? 900 : 800,
                        color: selected ? "#111827" : "#374151",
                        lineHeight: 1.35,
                      }}
                    >
                      {option.label}
                    </Typography>
                  }
                  sx={getAnswerOptionSx(selected)}
                />
              );
            })}
          </Box>
        </RadioGroup>

        {skipped && (
          <SkippedQuestionNotice label={skippedLabel} notice={skippedNotice} />
        )}

        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.5 }}>
          <Button
            variant="text"
            onClick={onSkip}
            disabled={saving || skipped}
            sx={skipQuestionButtonSx}
          >
            {skipLabel}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
